import React, { useEffect, useMemo, useState } from 'react';
import {
  Container, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Switch, Chip, Typography, Box, TextField, Tooltip, CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import Home from './Home';
import MailEditDialog from './MailEditDialog';
import {
  EmailTemplate, listerEmailTemplates, majEmailTemplate
} from '../services/emailTemplatesServices';

const categorieCouleur: Record<string, string> = {
  Compte: '#673ab7',
  Demande: '#03a9f4',
  Paiement: '#ff9800',
  'Visa livré': '#4caf50',
  'Inde tourisme': '#82B440',
  Interne: '#757575',
};

const MailsScreen: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<EmailTemplate | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      setTemplates(await listerEmailTemplates());
    } catch (e) {
      console.error('Erreur chargement templates', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return templates.filter(
      (t) =>
        t.libelle.toLowerCase().includes(q) ||
        (t.declencheur || '').toLowerCase().includes(q) ||
        (t.categorie || '').toLowerCase().includes(q)
    );
  }, [templates, search]);

  // Groupé par catégorie (ordre fourni par le backend)
  const groupes = useMemo(() => {
    const map = new Map<string, EmailTemplate[]>();
    filtered.forEach((t) => {
      const cat = t.categorie || 'Autre';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(t);
    });
    return Array.from(map.entries());
  }, [filtered]);

  const toggleActif = async (t: EmailTemplate, actif: boolean) => {
    // maj optimiste
    setTemplates((prev) => prev.map((x) => (x.code === t.code ? { ...x, actif: actif ? 1 : 0 } : x)));
    try {
      await majEmailTemplate(t.code, { actif });
    } catch (e) {
      console.error('Erreur toggle actif', e);
      setTemplates((prev) => prev.map((x) => (x.code === t.code ? { ...x, actif: actif ? 0 : 1 } : x)));
    }
  };

  const openEdit = (t: EmailTemplate) => {
    setSelected(t);
    setDialogOpen(true);
  };

  return (
    <>
      <Home />
      <Container maxWidth={false} sx={{ mt: 2, px: { xs: 1, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Mails</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
            Tous les mails envoyés par la plateforme. Cliquez sur ✏️ pour modifier le contenu.
          </Typography>
          <TextField
            label="Rechercher"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Box>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Mail</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Quand / pourquoi</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Destinataire</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Backend</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Actif</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Modifier</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupes.map(([cat, items]) => (
                  <React.Fragment key={cat}>
                    <TableRow>
                      <TableCell colSpan={6} sx={{ bgcolor: '#fafafa', py: 0.5 }}>
                        <Chip
                          label={cat}
                          size="small"
                          sx={{ bgcolor: categorieCouleur[cat] || '#757575', color: '#fff', fontWeight: 'bold' }}
                        />
                      </TableCell>
                    </TableRow>
                    {items.map((t) => (
                      <TableRow key={t.code} hover>
                        <TableCell sx={{ fontWeight: 500 }}>{t.libelle}</TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>{t.declencheur}</TableCell>
                        <TableCell>
                          <Chip label={t.destinataire_type || '—'} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={t.backend}
                            size="small"
                            color={t.backend === 'admin' ? 'primary' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={Number(t.actif) === 1 ? 'Envoi actif' : 'Envoi suspendu'}>
                            <Switch
                              size="small"
                              checked={Number(t.actif) === 1}
                              onChange={(e) => toggleActif(t, e.target.checked)}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" color="primary" onClick={() => openEdit(t)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Aucun mail
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>

      <MailEditDialog
        open={dialogOpen}
        template={selected}
        onClose={() => setDialogOpen(false)}
        onSaved={fetchTemplates}
      />
    </>
  );
};

export default MailsScreen;
